import { cn } from "@/lib/utils";

/**
 * Movement indicator for leaderboard-style rankings.
 *
 * The `value` is a *signed delta where positive means "moved up"* (improved
 * standing) — e.g. rank #5 → #2 yields +3. That matches the ARR-leaderboard
 * API, which computes `rank_change = beginning_rank - ending_rank`.
 *
 *   value > 0  → green ▲ with the magnitude   (climbed the board)
 *   value < 0  → red ▼ with the magnitude     (slipped down the board)
 *   value === 0 → muted – (held position)
 *   null/undef → muted em-dash placeholder    (not ranked in one period)
 *
 * Lives in the design system because before/after positional movement shows up
 * anywhere we rank things (ARR, usage, scores). Pair it with a magnitude that
 * already encodes "up is good"; if a metric needs "down is good", invert the
 * sign before passing it in rather than re-coloring here.
 */
export function RankDelta({
  value,
  className,
}: {
  value: number | null | undefined;
  className?: string;
}) {
  if (value == null) {
    return <span className={cn("text-muted-foreground", className)}>—</span>;
  }

  if (value === 0) {
    return (
      <span
        className={cn(
          "inline-flex items-center gap-1 text-xs font-medium text-muted-foreground tabular-nums",
          className,
        )}
      >
        <span aria-hidden>–</span>
        <span className="sr-only">No change in rank</span>
      </span>
    );
  }

  const up = value > 0;
  const magnitude = Math.abs(value);
  return (
    <span
      className={cn(
        "inline-flex items-center gap-0.5 text-xs font-semibold tabular-nums",
        up ? "text-success" : "text-danger",
        className,
      )}
    >
      <span aria-hidden className="text-[10px] leading-none">
        {up ? "▲" : "▼"}
      </span>
      {magnitude}
      <span className="sr-only">
        {up ? "up" : "down"} {magnitude} {magnitude === 1 ? "place" : "places"}
      </span>
    </span>
  );
}
